import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import LoadingError from "../components/LoadingError";

/**
 * ProtectedRoute component that enforces role and manufacturer-based access control
 *
 * @param {Object} props
 * @param {React.Component} props.component - Component to render if access is allowed
 * @param {Array<string>} props.allowedRoles - Array of roles that can access this route
 * @param {Array<string>} props.allowedManufacturers - Array of manufacturers that can access this route
 * @param {string} props.fallbackPath - Path to redirect to if access is denied (default: "/")
 */
const ProtectedRoute = ({
  component: Component,
  allowedRoles = [],
  allowedManufacturers = [],
  fallbackPath = "/",
}) => {
  const { user, hasCheckedAuth } = useAuth();

  // Still loading auth
  if (!hasCheckedAuth) {
    return <LoadingError isLoading={true} error={null} />;
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role permission
  const hasRoleAccess =
    allowedRoles.length === 0 || allowedRoles.includes(user.role);

  // Check manufacturer permission
  const hasManufacturerAccess =
    allowedManufacturers.length === 0 ||
    allowedManufacturers.includes(user.manufacturer);

  // Both checks must pass
  if (!hasRoleAccess || !hasManufacturerAccess) {
    console.warn(
      `Access denied: role=${user.role} (allowed: ${allowedRoles.join(
        ", "
      )}), manufacturer=${
        user.manufacturer
      } (allowed: ${allowedManufacturers.join(", ")})`
    );
    return <Navigate to={fallbackPath} replace />;
  }

  // All checks passed, render component
  return <Component />;
};

export default ProtectedRoute;
